// =====================================================================================
// INVRT Radar - web console - catalogue pager reader (Story PS1)
//
// The fail-closed E03 page parser and a single-page reader shared by the catalogue
// section's unified pager, the access section's assignment-dialog catalogue choosers, and
// any later PS consumer. Parsing is deliberately strict: a response that does not match
// the reviewed contract throws an ApiError the states layer can render, and nothing is
// invented in the browser.
// =====================================================================================

import { ApiError } from "@/lib/api";
import type { useApiClient } from "@/hooks/useApi";
import type {
  CatalogueItem,
  CatalogueKind,
  CatalogueMutationResponse,
  CataloguePage,
} from "@/lib/types.entitlements";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CODE_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;
const OFFSET_DATE_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(Z|[+-]\d{2}:\d{2})$/;
const ITEM_BASE_KEYS = [
  "id",
  "code",
  "name",
  "status",
  "version",
  "effective_from",
  "effective_to",
  "created_at",
  "updated_at",
] as const;
const ITEM_KEYS: Record<CatalogueKind, readonly string[]> = {
  product: ITEM_BASE_KEYS,
  service: [...ITEM_BASE_KEYS, "product_id"],
  module: [...ITEM_BASE_KEYS, "service_id"],
  campaign: [...ITEM_BASE_KEYS, "module_id"],
};
const PAGE_KEYS = ["items", "next_cursor"] as const;

export function invalidContract(message: string): ApiError {
  return new ApiError({
    token: "internal_error",
    message,
    httpStatus: 502,
    correlationId: null,
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStatus(value: unknown): value is CatalogueItem["status"] {
  return value === "active" || value === "inactive" || value === "retired";
}

function isDateTime(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const match = value.match(OFFSET_DATE_TIME_PATTERN);
  if (!match) return false;
  const [, yearText, monthText, dayText, hourText, minuteText, secondText, offsetText] = match;
  if (yearText === undefined || monthText === undefined || dayText === undefined || hourText === undefined || minuteText === undefined || secondText === undefined || offsetText === undefined) {
    return false;
  }
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const second = Number(secondText);
  const daysInMonth = [31, (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const maximumDay = daysInMonth[month - 1];

  if (maximumDay === undefined || month < 1 || month > 12 || day < 1 || day > maximumDay || hour > 23 || minute > 59 || second > 59) {
    return false;
  }
  if (offsetText === "Z") return true;
  const [offsetHourText, offsetMinuteText] = offsetText.slice(1).split(":");
  return Number(offsetHourText) <= 23 && Number(offsetMinuteText) <= 59;
}

function hasExactKeys(value: unknown, keys: readonly string[]): value is Record<string, unknown> {
  if (!isRecord(value)) return false;
  const actualKeys = Object.keys(value);
  return actualKeys.length === keys.length && actualKeys.every((key) => keys.includes(key));
}

function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

function isCode(value: unknown): value is string {
  return typeof value === "string" && value.length <= 64 && CODE_PATTERN.test(value);
}

function isName(value: unknown): value is string {
  return typeof value === "string" && value === value.trim() && value.length >= 1 && value.length <= 120;
}

function hasBaseFields(value: unknown): value is CatalogueItem {
  if (!isRecord(value)) return false;
  return (
    isUuid(value.id) &&
    isCode(value.code) &&
    isName(value.name) &&
    isStatus(value.status) &&
    Number.isSafeInteger(value.version) && Number(value.version) > 0 &&
    isDateTime(value.effective_from) &&
    (isDateTime(value.effective_to) || value.effective_to === null) &&
    isDateTime(value.created_at) &&
    isDateTime(value.updated_at)
  );
}

function isItemForKind(value: unknown, kind: CatalogueKind): value is CatalogueItem {
  if (!hasExactKeys(value, ITEM_KEYS[kind]) || !hasBaseFields(value)) return false;
  if (kind === "product") {
    return true;
  }
  if (kind === "service") {
    return isUuid(value.product_id);
  }
  if (kind === "module") {
    return isUuid(value.service_id);
  }
  return isUuid(value.module_id);
}

function normaliseItem(value: unknown, kind: CatalogueKind): CatalogueItem | null {
  if (!isItemForKind(value, kind) || !isRecord(value)) return null;
  const base = {
    id: value.id as string,
    code: value.code as string,
    name: value.name as string,
    status: value.status as CatalogueItem["status"],
    version: value.version as number,
    effective_from: value.effective_from as string,
    effective_to: value.effective_to as string | null,
    created_at: value.created_at as string,
    updated_at: value.updated_at as string,
  };
  if (kind === "product") return base;
  if (kind === "service") return { ...base, product_id: value.product_id as string };
  if (kind === "module") return { ...base, service_id: value.service_id as string };
  return { ...base, module_id: value.module_id as string };
}

export function parsePage(value: unknown, kind: CatalogueKind): CataloguePage | null {
  if (!hasExactKeys(value, PAGE_KEYS) || !Array.isArray(value.items)) return null;
  if (value.next_cursor !== null && (typeof value.next_cursor !== "string" || value.next_cursor.length < 1 || value.next_cursor.length > 512)) {
    return null;
  }
  const items = value.items.map((item) => normaliseItem(item, kind));
  return items.every((item): item is CatalogueItem => item !== null)
    ? { items, next_cursor: value.next_cursor }
    : null;
}

type ApiClient = ReturnType<typeof useApiClient>;

/** One reviewed E03 page; a malformed response fails closed as an ApiError. */
export async function fetchCataloguePage(
  api: ApiClient,
  kind: CatalogueKind,
  cursor?: string,
): Promise<CataloguePage> {
  const response = await api.get<unknown>(`/catalogue/${kind}`, {
    requiresOrg: false,
    includeOrg: false,
    query: { limit: 100, cursor },
  });
  const parsed = parsePage(response, kind);
  if (parsed === null) {
    throw invalidContract(`The ${kind} catalogue response did not match the reviewed contract.`);
  }
  return parsed;
}

/** Walks one kind's real cursors to exhaustion - for pickers that must offer every row. */
export async function readCatalogueKind(
  api: ApiClient,
  kind: CatalogueKind,
): Promise<CatalogueItem[]> {
  const items: CatalogueItem[] = [];
  const seen = new Set<string>();
  let cursor: string | undefined;
  for (;;) {
    const page = await fetchCataloguePage(api, kind, cursor);
    items.push(...page.items);
    if (page.next_cursor === null) return items;
    if (seen.has(page.next_cursor)) {
      throw invalidContract(`The ${kind} catalogue repeated a pagination cursor.`);
    }
    seen.add(page.next_cursor);
    cursor = page.next_cursor;
  }
}

const MUTATION_RESPONSE_KEYS = ["item", "correlation_id"] as const;

export function parseMutationResponse(value: unknown, kind: CatalogueKind): CatalogueMutationResponse | null {
  if (!hasExactKeys(value, MUTATION_RESPONSE_KEYS) || typeof value.correlation_id !== "string" || value.correlation_id.length < 1 || value.correlation_id.length > 200) {
    return null;
  }
  const item = normaliseItem(value.item, kind);
  return item === null ? null : { item, correlation_id: value.correlation_id };
}
